import React, { useEffect, useMemo, useState } from "react";

const normalizeSymbol = (value) => (
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[-/]/g, "_")
);

export const normalizeTradingPair = (pair) => {
  if (!pair) return null;
  const rawSymbol = typeof pair === "string" ? pair : pair.symbol || pair.pair || "";
  let symbol = normalizeSymbol(rawSymbol);
  if (!symbol) return null;

  let marketType = String(
    typeof pair === "string" ? "" : pair.market_type || pair.market || pair.type || ""
  ).trim().toUpperCase();
  if (["FUTURE", "PERP", "PERPETUAL"].includes(marketType)) marketType = "FUTURES";
  if (!["SPOT", "FUTURES"].includes(marketType)) {
    marketType = symbol.endsWith("_PERP") ? "FUTURES" : "SPOT";
  }

  if (marketType === "FUTURES" && !symbol.endsWith("_PERP")) {
    symbol = `${symbol}_PERP`;
  }
  if (marketType === "SPOT" && symbol.endsWith("_PERP")) {
    marketType = "FUTURES";
  }

  return {
    symbol,
    market_type: marketType,
    signal_webhook:
      typeof pair === "object"
        ? String(pair.signal_webhook || pair.webhook || pair.webhook_url || "").trim()
        : "",
    signal_type:
      typeof pair === "object" ? String(pair.signal_type || "").trim() : "",
  };
};

export const normalizeTradingPairs = (value, fallbackSymbol = "") => {
  const source = Array.isArray(value) ? value : [];
  const pairs = source
    .map(normalizeTradingPair)
    .filter(Boolean);

  if (!pairs.length && fallbackSymbol) {
    const fallback = normalizeTradingPair({ symbol: fallbackSymbol, market_type: "SPOT" });
    if (fallback) pairs.push(fallback);
  }

  const seen = new Set();
  return pairs.filter((pair) => {
    const key = `${pair.market_type}:${pair.symbol}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const stripPerp = (symbol) => normalizeSymbol(symbol).replace(/_PERP$/, "");

const getSymbolsByMarket = (symbolOptions, marketType) => {
  if (Array.isArray(symbolOptions)) {
    return symbolOptions;
  }
  if (!symbolOptions || typeof symbolOptions !== "object") {
    return [];
  }
  if (symbolOptions.futures_enabled === false && marketType === "FUTURES") {
    return [];
  }
  const key = marketType === "FUTURES" ? "futures_symbols" : "spot_symbols";
  const marketSymbols = symbolOptions[key];
  if (Array.isArray(marketSymbols) && marketSymbols.length) {
    return marketSymbols;
  }
  return Array.isArray(symbolOptions.symbols) ? symbolOptions.symbols : [];
};

const TradingPairsSelector = ({
  value = [],
  onChange,
  symbolOptions = [],
  symbolsLoading = false,
  symbolsError = "",
  onRefreshSymbols,
  disabled = false,
}) => {
  const pairs = normalizeTradingPairs(value);
  const futuresDisabled =
    !Array.isArray(symbolOptions) && symbolOptions?.futures_enabled === false;
  const [draftSymbol, setDraftSymbol] = useState("");
  const [draftMarketType, setDraftMarketType] = useState("SPOT");
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    if (futuresDisabled) setDraftMarketType("SPOT");
  }, [futuresDisabled]);

  const filteredSymbols = useMemo(() => {
    const query = normalizeSymbol(draftSymbol);
    const base = getSymbolsByMarket(symbolOptions, draftMarketType);
    const normalized = base.map((item) => normalizeSymbol(item)).filter(Boolean);
    const filtered = query
      ? normalized.filter((item) => item.includes(query) || stripPerp(item).includes(query))
      : normalized;
    return [...new Set(filtered)].slice(0, 60);
  }, [draftSymbol, draftMarketType, symbolOptions]);

  const emit = (nextPairs) => {
    onChange?.(normalizeTradingPairs(nextPairs));
  };

  const addPair = (symbolInput = draftSymbol, marketInput = draftMarketType) => {
    const effectiveMarket = futuresDisabled ? "SPOT" : marketInput;
    const pair = normalizeTradingPair({ symbol: symbolInput, market_type: effectiveMarket });
    if (!pair) return;
    const allowed = getSymbolsByMarket(symbolOptions, pair.market_type).map(normalizeSymbol);
    if (allowed.length && !allowed.includes(pair.symbol)) return;
    emit([...pairs, pair]);
    setDraftSymbol("");
    setShowOptions(false);
  };

  const removePair = (index) => {
    emit(pairs.filter((_, idx) => idx !== index));
  };

  const updatePairMarket = (index, nextMarketType) => {
    const effectiveMarket = futuresDisabled ? "SPOT" : nextMarketType;
    emit(pairs.map((pair, idx) => (
      idx === index
        ? normalizeTradingPair({
            symbol: stripPerp(pair.symbol),
            market_type: effectiveMarket,
            signal_webhook: pair.signal_webhook,
            signal_type: pair.signal_type,
          })
        : pair
    )));
  };

  const updatePairWebhook = (index, field, value) => {
    emit(pairs.map((pair, idx) => (
      idx === index ? { ...pair, [field]: value } : pair
    )));
  };

  return (
    <div className="trading-pairs-selector">
      <div className="trading-pairs-list">
        {pairs.length ? pairs.map((pair, index) => (
          <div className="trading-pair-chip" key={`${pair.market_type}:${pair.symbol}:${index}`}>
            <span className={`trading-pair-market ${pair.market_type === "FUTURES" ? "is-futures" : "is-spot"}`}>
              {pair.market_type === "FUTURES" ? "Futuros" : "Spot"}
            </span>
            <strong>{pair.symbol}</strong>
            <select
              value={pair.market_type}
              onChange={(event) => updatePairMarket(index, event.target.value)}
              disabled={disabled}
              aria-label={`Tipo de mercado para ${pair.symbol}`}
            >
              <option value="SPOT">Spot</option>
              {!futuresDisabled && <option value="FUTURES">Futuros</option>}
            </select>
            <button type="button" onClick={() => removePair(index)} disabled={disabled} aria-label={`Quitar ${pair.symbol}`}>
              ×
            </button>
            {pair.market_type === "FUTURES" ? (
              <div className="trading-pair-webhook">
                <label>
                  <span>Webhook Signal Bot (futuros)</span>
                  <input
                    type="text"
                    placeholder="https://www.pionex.com/signal/api/v1/signal_listener/trading_view?token=..."
                    value={pair.signal_webhook || ""}
                    onChange={(event) => updatePairWebhook(index, "signal_webhook", event.target.value)}
                    disabled={disabled}
                  />
                </label>
                <label>
                  <span>signal_type (UUID del bot)</span>
                  <input
                    type="text"
                    placeholder="00000000-0000-0000-0000-000000000000"
                    value={pair.signal_type || ""}
                    onChange={(event) => updatePairWebhook(index, "signal_type", event.target.value)}
                    disabled={disabled}
                  />
                </label>
                <details className="trading-pair-webhook-help">
                  <summary>¿De dónde saco el webhook y el signal_type?</summary>
                  <ol>
                    <li>Ve a <strong>Pionex → Bots → Signal → Crear señal (TradingView)</strong>.</li>
                    <li>Copia la <strong>Webhook URL</strong> que genera Pionex (formato <code>https://www.pionex.com/signal/api/v1/signal_listener/trading_view?token=...</code>).</li>
                    <li>Copia el <strong>Message template</strong> y extrae el valor del campo <code>signal_type</code> (es el UUID del bot).</li>
                    <li>En la Alerta de TradingView activa la Notificación <em>Webhook URL</em> con esos mismos valores.</li>
                    <li>Pega aquí la URL y el <code>signal_type</code> y guarda el perfil.</li>
                  </ol>
                </details>
              </div>
            ) : null}
          </div>
        )) : (
          <div className="trading-pairs-empty">Agrega uno o más pares para operar.</div>
        )}
      </div>

      <div className="trading-pairs-add">
        <select
          value={draftMarketType}
          onChange={(event) => setDraftMarketType(event.target.value)}
          disabled={disabled}
          aria-label="Tipo de mercado"
        >
          <option value="SPOT">Spot</option>
          <option value="FUTURES">Futuros</option>
        </select>
        <div className="symbol-select trading-pair-symbol">
          <div className="symbol-select-input">
            <input
              type="text"
              placeholder={draftMarketType === "FUTURES" ? "BTC_USDT" : "SOL_USDT"}
              value={draftSymbol}
              onChange={(event) => {
                setDraftSymbol(event.target.value);
                setShowOptions(true);
              }}
              onFocus={() => setShowOptions(true)}
              onBlur={() => setTimeout(() => setShowOptions(false), 120)}
              disabled={disabled}
            />
            <button
              type="button"
              className="symbol-select-refresh"
              onClick={() => onRefreshSymbols?.()}
              disabled={disabled || symbolsLoading}
              title="Actualizar pares"
            >
              {symbolsLoading ? "..." : "↻"}
            </button>
          </div>
          {symbolsError ? <span className="symbol-select-error">{symbolsError}</span> : null}
          {showOptions && filteredSymbols.length > 0 ? (
            <ul className="symbol-select-options">
              {filteredSymbols.map((item) => {
                const baseSymbol = stripPerp(item);
                const finalSymbol = draftMarketType === "FUTURES" ? normalizeSymbol(item) : baseSymbol;
                return (
                  <li key={`${draftMarketType}:${item}`}>
                    <button
                      type="button"
                      className="symbol-select-option"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        addPair(finalSymbol, draftMarketType);
                      }}
                    >
                      <span>{finalSymbol}</span>
                      <small>{draftMarketType === "FUTURES" ? "Futuros" : "Spot"}</small>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          className="trading-pairs-add-btn"
          onClick={() => addPair()}
          disabled={disabled || !normalizeSymbol(draftSymbol)}
        >
          Agregar
        </button>
      </div>
    </div>
  );
};

export default TradingPairsSelector;
